import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { useMyAccounts } from '../../hooks/useMyAccounts';
import { Button, MenuItem } from '../ui';
import type { MyAccountDto } from '../../business/auth/AccountSessionManager';

/**
 * Navbar affordance for people who own more than one account, naming the account they are
 * currently in.
 *
 * The same switcher already lives inside the user menu, but that is two clicks deep and gives no
 * hint from the outside that anything is switchable. People with near-identical account names
 * ("Texas Star Painting" and "Texas Star Painting of Austin") could not tell which one they were
 * looking at without opening the menu — so the current account is spelled out in the bar itself,
 * and each row carries its subdomain to break the tie between similar names.
 *
 * Desktop only: on phones the navbar has no room for a named button, and the user menu keeps
 * carrying the switcher there.
 */
const AccountSwitcherNavButton: React.FC = () => {
  const { accounts, hasMultiple, current, switchingTo, switchTo } = useMyAccounts();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!hasMultiple) return null;

  const handleSelect = (account: MyAccountDto) => {
    if (account.isCurrent) {
      setIsOpen(false);
      return;
    }
    // Left open on purpose: the row it came from is the only place the in-flight spinner shows.
    void switchTo(account);
  };

  return (
    <div className="hidden md:block relative" ref={containerRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(open => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={`
          gap-1.5 px-2.5 max-w-[220px] text-13
          border-accent-200 bg-accent-50 text-accent-700
          hover:bg-accent-100 hover:border-accent-300
          focus-visible:ring-accent-500/40
          ${isOpen ? 'bg-accent-100 border-accent-300' : ''}
        `}
      >
        <Building2 className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <span className="truncate">{current?.tenantName ?? 'Switch account'}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-none transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </Button>

      {isOpen && (
        <div
          role="menu"
          className="
            absolute right-0 mt-1.5 w-64
            bg-surface
            rounded-lg shadow-lg shadow-ink-200/50
            border border-ink-200/80
            overflow-hidden
            z-50
            animate-in fade-in slide-in-from-top-1 duration-150
          "
        >
          <div className="px-3 py-2 text-11 font-semibold uppercase tracking-wide text-ink-400 bg-ink-50/50 border-b border-ink-100">
            Switch account
          </div>

          <div className="p-1.5">
            {accounts.map(account => {
              const isSwitching = switchingTo === account.tenantId;

              return (
                <MenuItem
                  key={account.tenantId}
                  onClick={() => handleSelect(account)}
                  // Only disabled while a switch is in flight. The current account stays undimmed —
                  // MenuItem fades disabled rows, and fading the account you are in reads wrong.
                  disabled={switchingTo !== null}
                  aria-current={account.isCurrent ? 'true' : undefined}
                  className="rounded-md"
                >
                  {isSwitching ? (
                    <Loader2 className="h-4 w-4 text-ink-400 animate-spin shrink-0" />
                  ) : (
                    <Building2 className="h-4 w-4 text-ink-400 shrink-0" />
                  )}
                  <span className="flex-1 min-w-0 text-left">
                    <span className="block truncate text-13 text-ink-800">{account.tenantName}</span>
                    {account.subdomain && (
                      <span className="block truncate text-11 text-ink-400">{account.subdomain}</span>
                    )}
                  </span>
                  {account.isCurrent && <Check className="h-4 w-4 text-accent-600 shrink-0" />}
                </MenuItem>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcherNavButton;
